import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, Trophy, CheckCircle2, Smile, Heart, BookOpen, CalendarDays, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { useWellnessStreak } from '@/hooks/useWellnessStreak';
import { haptics } from '@/lib/haptics';
import { cn } from '@/lib/utils';

const MILESTONES = [7, 14, 30, 50, 100, 200, 300, 365];

const HeaderStreakButton: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { streakData, loading, hasCheckedInToday } = useWellnessStreak();

  const currentStreak = streakData?.current_streak || 0;
  const longestStreak = streakData?.longest_streak || 0;
  const totalCheckIns = streakData?.total_check_ins || 0;
  const nextMilestone = MILESTONES.find((m) => currentStreak < m);
  const daysToNext = nextMilestone ? nextMilestone - currentStreak : 0;

  const flameColor =
    currentStreak >= 30
      ? 'text-red-500'
      : currentStreak >= 14
      ? 'text-orange-500'
      : currentStreak >= 7
      ? 'text-amber-500'
      : 'text-orange-400';

  const go = (path: string) => {
    haptics.light();
    setOpen(false);
    navigate(path);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) haptics.light();
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Wellness streak"
          data-community-tour="streak"
          className={cn(
            'flex items-center gap-1 h-8 px-2 rounded-full border border-orange-200 dark:border-orange-800/60',
            'bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/40 dark:to-amber-950/40',
            'transition-transform duration-200 active:scale-95 hover:shadow-soft'
          )}
        >
          <Flame
            className={cn(
              'h-4 w-4',
              flameColor,
              hasCheckedInToday && currentStreak > 0 && 'animate-pulse'
            )}
          />
          {!loading && currentStreak > 0 && (
            <span className={cn('text-xs font-bold', flameColor)}>{currentStreak}</span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-72 bg-background z-50 p-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 shadow-lg">
            <Flame className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className={cn('text-2xl font-bold', flameColor)}>{currentStreak}</span>
              <span className="text-sm text-muted-foreground">day Vent Streak</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {hasCheckedInToday
                ? "You've checked in today."
                : 'Check in today to keep your streak.'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-4 text-center">
          <div>
            <div className="flex items-center justify-center gap-1 text-amber-600 dark:text-amber-400">
              <Trophy className="h-4 w-4" />
              <span className="font-semibold text-sm">{longestStreak}</span>
            </div>
            <span className="text-[11px] text-muted-foreground">Best</span>
          </div>
          <div>
            <div className="flex items-center justify-center gap-1 text-primary">
              <CheckCircle2 className="h-4 w-4" />
              <span className="font-semibold text-sm">{totalCheckIns}</span>
            </div>
            <span className="text-[11px] text-muted-foreground">Check-ins</span>
          </div>
          <div>
            <div className="flex items-center justify-center gap-1 text-foreground">
              <span className="font-semibold text-sm">{nextMilestone ? daysToNext : '—'}</span>
            </div>
            <span className="text-[11px] text-muted-foreground">
              {nextMilestone ? `to ${nextMilestone}d` : 'Maxed'}
            </span>
          </div>
        </div>

        <Separator className="my-3" />

        <div className="space-y-1">
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 h-9"
            onClick={() => go('/app?tool=journal')}
          >
            <Smile className="h-4 w-4 text-primary" />
            Daily Journal
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 h-9"
            onClick={() => go('/app?tool=selfcare')}
          >
            <Heart className="h-4 w-4 text-primary" />
            Self-Care Checklist
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 h-9"
            onClick={() => go('/app?tool=coping')}
          >
            <BookOpen className="h-4 w-4 text-primary" />
            Coping Strategies
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 h-9"
            onClick={() => go('/app?tool=progress')}
          >
            <Timer className="h-4 w-4 text-primary" />
            My Progress
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 h-9"
            onClick={() => go('/app/wellness/calendar')}
          >
            <CalendarDays className="h-4 w-4 text-primary" />
            Wellness Calendar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default HeaderStreakButton;
